import * as _ from 'underscore';
import * as queryString from 'query-string';

// NOTE: This is only a subset of specs that are needed in VizPortal
export interface OpenWindowSpecs {
  width?: number;
  height?: number;
  toolbar?: number;
  resizable?: number;
  top?: number;
  left?: number;
}

/**
 * Contains window.location behavior not covered by angular's $location service.
 *
 * Why? The browser's window.location cannot be mocked. This adds one level of
 * indirection between app code and window.location so we can mock for testing.
 */

// Download the file at the uri
function download(uri: string) {
  navigateTo(uri);
}

// Open the given uri in a new window
function open(uri: string, windowName: string = '_blank', specs?: OpenWindowSpecs) {
  return window.open(uri, windowName, formatWindowOpenSpecs(specs));
}

function formatWindowOpenSpecs(specs: OpenWindowSpecs): string {
  return _.map(<_.Dictionary<any>>specs, (val: any, key: string) => (key + '=' + val)).join(',');
}

/**
 * Sets the location to the given uri
 */
function navigateTo(uri: string) {
  window.location.href = uri;
}

/**
 * Set the location of the given iframe
 */
function navigateIFrameTo(iframe: HTMLIFrameElement, uri: string) {
  iframe.contentWindow.location.href = uri;
}

function navigateToWithoutAddingToHistory(uri: string) {
  window.location.replace(uri);
}

/**
 * Cross-browser method for getting window.location.origin. Example:
 * window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 * return value         : 'scheme://domain:port'
 */
function getOrigin() {
  return window.location.protocol + '//' + window.location.host;
}

/**
 * Get everything after the host in a url.  Example:
 *
 *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 *    return value         : '/path?query_string#fragment_id'
 */
function getPathQueryFragment() {
  return window.location.href.split(window.location.host)[1];
}

/**
 * Get path and query components of url.  Example:
 *
 *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 *    return value         : '/path?query_string'
 */
function getPathQuery() {
  return getPathQueryFragment().replace(window.location.hash, '');
}

/**
 * Set everything after the host in a url. Example:
 *
 *    pathQueryFragment    : '/path?query_string#fragment_id'
 *    window.location.href : 'scheme://domain:port/path?query_string#fragment_id'
 */
function setPathQueryFragment(pathQueryFragment: string) {
  window.location.href = getOrigin() + (/^\//.test(pathQueryFragment) ? '' : '/') + pathQueryFragment;
}

/**
 * Updates a query parameter in the hash of the url. Example:
 * beforeUrl: #/explore?order=ownerName:asc,name:asc
 *    queryParameterKey : 'order'
 *    value : 'ownerName:asc,name:asc'
 * afterUrl: #/explore?order=createdAt:desc,ownerName:asc,name:asc
 */
function setHashQueryParameter(queryParameterKey: string, value: string) {
  const urlParts = queryString.parseUrl(window.location.hash);
  urlParts.query[queryParameterKey] = value;
  const newQueryString = queryString.stringify(urlParts.query);
  window.location.hash = urlParts.url + '?' + newQueryString;
}

function updateHashQueryParameterWithReplace(queryParameterKey: string, value: string, options? : queryString.StringifyOptions) {
  const urlParts = queryString.parseUrl(window.location.hash);
  urlParts.query[queryParameterKey] = value;
  const newQueryString = options? queryString.stringify(urlParts.query, options) : queryString.stringify(urlParts.query);
  window.location.replace(urlParts.url + '?' + newQueryString);
}

function deleteQueryParameterWithReplace(queryParameterKey: string, options? : queryString.StringifyOptions){
  const urlParts = queryString.parseUrl(window.location.hash);
  delete urlParts.query[queryParameterKey];
  const newQueryString = options? queryString.stringify(urlParts.query, options) : queryString.stringify(urlParts.query);
  window.location.replace(urlParts.url + '?' + newQueryString);
}

function deleteQueryParameter(queryParameterKey: string, options? : queryString.StringifyOptions){
  const urlParts = queryString.parseUrl(window.location.hash);
  delete urlParts.query[queryParameterKey];
  const newQueryString = options? queryString.stringify(urlParts.query, options) : queryString.stringify(urlParts.query);
  window.location.hash = urlParts.url + '?' + newQueryString;
}

/**
 * Get path and query components of url.  Example:
 *
 *    window.location.href : 'scheme://domain:port/#/explore?order=ownerName:asc,name:asc'
 *    return value         : '{order: ownerName:asc,name:asc}'
 */
function getHashQueryObject(): object {
  let hashQueryObject = {};
  const hash = window.location.hash;
  const queryStart = hash.indexOf('?');
  if(queryStart === -1){
    return hashQueryObject;
  }
  const hashQuery = hash.slice(queryStart);
  hashQueryObject = queryString.parse(hashQuery);
  return hashQueryObject;
}

function reload() {
  window.location.reload();
}

/**
 * Wrapper to stub for unit tests
 */
function getHost(){
  return window.location.host;
}

/**
 * Wrapper to stub for unit tests
 */
function getProtocol(){
  return window.location.protocol;
}

/**
 * Wrapper to stub for unit tests
 */
function getHref() {
  return window.location.href;
}

export const WindowLocationService = {
  download,
  open,
  navigateTo,
  navigateIFrameTo,
  navigateToWithoutAddingToHistory,
  getOrigin,
  getPathQueryFragment,
  getPathQuery,
  setPathQueryFragment,
  setHashQueryParameter,
  updateHashQueryParameterWithReplace,
  deleteQueryParameterWithReplace,
  deleteQueryParameter,
  reload,
  getHost,
  getProtocol,
  getHref,
  getHashQueryObject
};

export type WindowLocationService = typeof WindowLocationService;
